import {
  forwardRef,
  type ReactNode,
  type PropsWithChildren,
  type ReactElement,
  Children,
} from 'react';

import {
  _useAriaLabelingProps,
  type AriaLabelingProps,
  type DataTestId,
  type MaskingProps,
} from '@dynatrace/strato-components/core';
import { Flex, useBreakpoint } from '@dynatrace/strato-components/layouts';

import {
  Description,
  type TerminologyDescriptionProps,
} from './Description.js';
import { Footer } from './Footer.js';
import { TerminologyOverlayContext } from './TerminologyOverlayContext.js';
import { Trigger } from './Trigger.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { isDev } from '../../core/utils/is-node-environment.js';
import { isSlot } from '../../core/utils/is-slot.js';
import { useOverlayWithTrigger } from '../hooks/useOverlayWithTrigger.js';
import { Overlay } from '../overlay/Overlay.js';

// #region Typings
/**
 * @public
 */
export interface TerminologyOverlayProps
  extends AriaLabelingProps,
    DataTestId,
    MaskingProps {
  /**
   * Whether the overlay is open by default
   * @defaultValue false
   */
  defaultOpen?: boolean;
  /**
   * The component accepts TerminologyOverlay.Trigger, TerminologyOverlay.Description, and TerminologyOverlay.Footer as children.
   */
  children: ReactNode;
}

/** Defines slots into which children of the Terminology Overlay should be rendered. */
type TerminologyOverlaySlots = {
  trigger: ReactNode;
  description: ReactElement<TerminologyDescriptionProps> | null;
  footer: ReactElement<PropsWithChildren>[];
};
// #endregion Typings

/**
 * Outputs Terminology overlay warring messages, by writing them to the console in development environments
 */
function outputMessage(message: string) {
  if (message && isDev()) {
    // eslint-disable-next-line no-console
    console.warn(message);
  }
}

const TerminologyOverlayRoot = /* @__PURE__ */ forwardRef<
  HTMLButtonElement,
  TerminologyOverlayProps
>((props, forwardedRef) => {
  const {
    children,
    defaultOpen,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    ...remainingProps
  } = props;
  const ariaProps = _useAriaLabelingProps(remainingProps);
  const smallerScreens = useBreakpoint('(max-width: 480px)');
  const {
    targetRef,
    triggerRef,
    overlayRef,
    overlayState,
    overlayProps,
    overlayTriggerProps,
  } = useOverlayWithTrigger({
    placement: 'bottom-start',
    placementFallbacks: ['bottom-end', 'top-start', 'top-end'],
    offset: 4,
    crossOffset: -16,
    widthStrategy: smallerScreens ? { min: 200 } : { min: 350 },
    defaultOpen,
    shouldCloseOnBlur: true,
  });

  const slots: TerminologyOverlaySlots = {
    description: null,
    footer: [],
    trigger: null,
  };

  Children.forEach(children, (child) => {
    if (isSlot(child, Trigger)) {
      slots.trigger = child.props.children;
    } else if (isSlot(child, Description)) {
      slots.description = child;
    } else if (isSlot(child, Footer)) {
      slots.footer.push(child);
    } else {
      outputMessage(
        'Only Trigger, Footer, and Description are valid children of the TerminologyOverlay component.',
      );
    }
  });
  const rootRef = useMergeRefs([forwardedRef, triggerRef, targetRef]);

  if (slots.trigger === null) {
    outputMessage('Trigger element has not been defined!');
  }

  return (
    <TerminologyOverlayContext.Provider value={overlayTriggerProps}>
      <Trigger
        ref={rootRef}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
      >
        {slots.trigger}
      </Trigger>
      <Overlay
        ref={overlayRef}
        isOpen={overlayState.isOpen}
        overlayProps={overlayProps}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        {...ariaProps}
      >
        <Flex paddingY={12} paddingX={16} flexDirection="column">
          {slots.description}
          {slots.footer}
        </Flex>
      </Overlay>
    </TerminologyOverlayContext.Provider>
  );
});

(
  TerminologyOverlayRoot as typeof TerminologyOverlayRoot & {
    displayName: string;
  }
).displayName = 'TerminologyOverlay';

/**
 * The `TerminologyOverlay` allows explaining some content in an additional overlay.
 * The element is marked, and a click opens the overlay. The `TerminologyOverlay`
 * consists of at least two elements, its trigger and the corresponding description.
 * @public
 */
export const TerminologyOverlay = Object.assign(TerminologyOverlayRoot, {
  Description,
  Trigger,
  Footer,
});
